export type AdminDocFrontmatter = {
    adminRoute?: string;
    includesWorkspaceDefinitionsReference?: boolean;
    openApiPathPrefixes?: string[];
    relatedOpenApiPathPrefixes?: string[];
    requiredPlatformAccess?: string;
    /** Sidebar-aligned grouping on the docs index, e.g. "Identity & platform". */
    section?: string;
    slug?: string;
    summary?: string;
    title?: string;
};

export type ParsedAdminDocMarkdown = {
    frontmatter: AdminDocFrontmatter;
    markdown: string;
};

const FRONTMATTER_REGEX = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/;

function parseFrontmatterValue(rawValue: string): string | boolean | string[] {
    const trimmedValue = rawValue.trim();
    if (trimmedValue === "true") {
        return true;
    }
    if (trimmedValue === "false") {
        return false;
    }
    if (trimmedValue.startsWith("[") && trimmedValue.endsWith("]")) {
        const inner = trimmedValue.slice(1, -1).trim();
        if (inner === "") {
            return [];
        }
        return inner
            .split(",")
            .map((item) => item.trim())
            .filter((item) => item.length > 0)
            .map((item) => item.replace(/^['"]|['"]$/g, ""));
    }
    return trimmedValue.replace(/^['"]|['"]$/g, "");
}

function parseFrontmatterBlock(frontmatterBlock: string): AdminDocFrontmatter {
    const frontmatter: AdminDocFrontmatter = {};
    let currentListKey: keyof AdminDocFrontmatter | null = null;

    for (const line of frontmatterBlock.split("\n")) {
        const trimmedLine = line.trim();
        if (trimmedLine === "") {
            continue;
        }

        const listItemMatch = trimmedLine.match(/^- (.+)$/);
        if (listItemMatch !== null && currentListKey !== null) {
            const listValue = listItemMatch[1];
            if (listValue === undefined) {
                continue;
            }
            const currentList = frontmatter[currentListKey];
            if (Array.isArray(currentList) === true) {
                currentList.push(listValue.replace(/^['"]|['"]$/g, ""));
            }
            continue;
        }

        const keyValueMatch = trimmedLine.match(/^([A-Za-z0-9_]+):\s*(.*)$/);
        if (keyValueMatch === null) {
            continue;
        }

        const [, rawKey, rawValue] = keyValueMatch;
        if (rawKey === undefined) {
            continue;
        }
        const key = rawKey as keyof AdminDocFrontmatter;
        currentListKey = null;

        if (rawValue === undefined || rawValue === "") {
            currentListKey = key;
            frontmatter[key] = [] as never;
            continue;
        }

        frontmatter[key] = parseFrontmatterValue(rawValue) as never;
    }

    return frontmatter;
}

function extractTitleFromMarkdown(markdown: string): string | null {
    const match = markdown.match(/^#\s+(.+)$/m);
    if (match === null) {
        return null;
    }
    return match[1]?.trim() ?? null;
}

function extractSummaryFromMarkdown(markdown: string): string {
    const lines = markdown.split("\n");
    let passedHeading = false;

    for (const line of lines) {
        const trimmedLine = line.trim();
        if (trimmedLine.startsWith("#")) {
            passedHeading = true;
            continue;
        }
        if (passedHeading !== true || trimmedLine === "" || trimmedLine.startsWith("|") || trimmedLine.startsWith("-")) {
            continue;
        }
        return trimmedLine;
    }

    return "Platform admin module documentation.";
}

export function parseAdminDocMarkdown(rawContent: string): ParsedAdminDocMarkdown {
    const match = rawContent.match(FRONTMATTER_REGEX);
    if (match === null) {
        return {
            frontmatter: {},
            markdown: rawContent.trim(),
        };
    }

    return {
        frontmatter: parseFrontmatterBlock(match[1] ?? ""),
        markdown: (match[2] ?? "").trim(),
    };
}

export function resolveAdminDocMetadata({
    slug,
    frontmatter,
    markdown,
}: {
    slug: string;
    frontmatter: AdminDocFrontmatter;
    markdown: string;
}): {
    adminRoute: `/a/${string}`;
    apiReferenceGroups: Array<{label: string; pathPrefixes: string[]}>;
    includesWorkspaceDefinitionsReference: boolean;
    markdown: string;
    openApiPathPrefixes: string[];
    relatedOpenApiPathPrefixes: string[];
    requiredPlatformAccess?: string;
    section: string;
    sidebarLabel: string;
    slug: string;
    summary: string;
    title: string;
} {
    const title = frontmatter.title ?? extractTitleFromMarkdown(markdown) ?? slug;
    const adminRoute = (frontmatter.adminRoute ?? `/a/${slug}`) as `/a/${string}`;
    const defaultOpenApiPrefix = `/admin/${slug}`;
    const section = frontmatter.section?.trim();

    return {
        slug: frontmatter.slug ?? slug,
        title,
        sidebarLabel: title,
        adminRoute,
        summary: frontmatter.summary ?? extractSummaryFromMarkdown(markdown),
        markdown,
        openApiPathPrefixes: frontmatter.openApiPathPrefixes ?? [defaultOpenApiPrefix],
        relatedOpenApiPathPrefixes: frontmatter.relatedOpenApiPathPrefixes ?? [],
        apiReferenceGroups: [],
        requiredPlatformAccess: frontmatter.requiredPlatformAccess,
        includesWorkspaceDefinitionsReference: frontmatter.includesWorkspaceDefinitionsReference === true,
        section: section === undefined || section === "" ? "Other" : section,
    };
}
